'use client';
import {
  CatBadge,
  CatButton,
  CatNumericInput,
  CatTable,
  CatTableCell,
  CatTableHeader,
  CatTableHeaderCell,
  CatTableObject,
  CatTableRow,
  CatTextLink
} from 'components/blocks-components';
import { useTranslations } from 'next-intl';
import { Components } from 'services/fbtItems/types';
import { Image } from 'components/image';
import { CatIconShopFilled } from '@blocks-react/components/Icons/ShopFilled';
import Price from 'components/price';
import CommonAvailabilityWrapper from '../common-availability-wrapper';
import styles from '../customize-kit-button/customize-kit-button.module.scss';
import {
  DealerPriceAvailabilityResponse,
  ProductDealerPriceAvailability
} from 'services/dealerPriceAvailability/types';
import { DealerResponse } from 'services/dealer';
import { LocationResponse } from 'services/location/types';
import { useParams } from 'next/navigation';
import { getSeoUrl } from 'utils/links';
import { CartResponse, OrderItemsResponseKit } from 'services/cart/types';
import { SkeletonLoader } from '@cat-ecom/pcc-components';
import { createHandleSelectItemEventGa } from './utils';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
export interface KitDetailsTableBodyProps {
  priceAvailabilityDrawerData?: DealerPriceAvailabilityResponse;
  kitData?: Components[];
  valHandle?: (e: CustomEvent, i: number) => void;
  dealerInformation?: DealerResponse;
  availRemoveInterimStockFlag?: boolean;
  isShowSourcingDetailEnabled?: boolean;
  isDeliveryEnabled?: boolean;
  userStoreLocationData?: LocationResponse;
  isHeadlessPDPEnabled?: boolean;
  handleAddToCart?: (item?: Array<Components[]> | Components) => void;
  orderItems?: Array<OrderItemsResponseKit>;
  contactDealerComp?: React.ReactNode;
  storeId?: string;
  dealerPriceData?: Record<string, ProductDealerPriceAvailability>;
  locale?: string;
  isLoadingInitial?: boolean;
  isLoadingRow?: Record<string, boolean>;
  mergedKitResponse;
  cartDetails?: CartResponse;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
}
const KitDetailsTableBody = ({
  priceAvailabilityDrawerData,
  kitData,
  valHandle,
  dealerInformation,
  availRemoveInterimStockFlag,
  isShowSourcingDetailEnabled,
  isDeliveryEnabled,
  locale,
  userStoreLocationData,
  isHeadlessPDPEnabled,
  handleAddToCart,
  orderItems,
  contactDealerComp,
  storeId,
  dealerPriceData,
  isLoadingInitial,
  isLoadingRow,
  mergedKitResponse,
  cartDetails,
  isRTL = false,
  currencyLdFeatureFlag = false
}: KitDetailsTableBodyProps) => {
  const t = useTranslations();
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const combinedData = kitData.map((item1) => {
    const item2 = priceAvailabilityDrawerData?.priceAndAvailability.find(
      (item) => item.partNumber === item1.partNumber
    );
    return { ...item1, price: item2 };
  });
  const params = useParams();
  const esiteUrl = getSeoUrl(params);
  const getItemCount = (partNumber): string => {
    const itemCount = orderItems?.find((a) => a.partNumber === partNumber);
    return itemCount?.quantity?.toString() ?? '0';
  };

  const handleSelectItemEventGa = (clickedItem) => {
    createHandleSelectItemEventGa(
      clickedItem,
      mergedKitResponse,
      cartDetails,
      storeId,
      fireSelectItemEvent
    );
  };

  return (
    <div>
      <CatTableObject height="100%">
        <CatTable>
          <CatTableHeader>
            <CatTableRow>
              <CatTableHeaderCell width="13%">
                {t('SSRO_QUANTITY')}
              </CatTableHeaderCell>
              <CatTableHeaderCell width="11%">
                {t('CPG_PRODUCT_IMAGE')}
              </CatTableHeaderCell>
              <CatTableHeaderCell>{t('PARTNUMBER_NAME')}</CatTableHeaderCell>
              <CatTableHeaderCell width="15%">
                {t('PDP_AVAILBLE_FOR_PICKUP')}
              </CatTableHeaderCell>
              <CatTableHeaderCell align="right" width="11%">
                {t('TOTAL_PRICE')}
              </CatTableHeaderCell>
              <CatTableHeaderCell width="12%"></CatTableHeaderCell>
              <CatTableHeaderCell width="6%"></CatTableHeaderCell>
            </CatTableRow>
          </CatTableHeader>
          {combinedData?.map((item, index) => (
            <CatTableRow key={item.name}>
              <CatTableCell>
                <CatNumericInput
                  value={Number(item.quantity)}
                  onBlNumericInputChange={(e) => valHandle(e, index)}
                  hideLabel
                  size="sm"
                  min={1}
                  max={99999}
                  data-testid="item-quantity-desktop"
                  status={Number(item.quantity) < 1 ? 'error' : null}
                  disabled={
                    !item?.price?.enableAddToCart || isLoadingRow[index]
                  }
                >
                  {Number(item.quantity) < 1 && (
                    <span slot="error">{t('INVALID_QUANTITY')}</span>
                  )}
                </CatNumericInput>
              </CatTableCell>
              <CatTableCell>
                <Image src={item.thumbnail} alt="img1" width="82" height="72" />
              </CatTableCell>
              <CatTableCell>
                <CatTextLink
                  href={
                    isHeadlessPDPEnabled
                      ? item?.partNumber
                      : `${esiteUrl}/${item?.partNumber}`
                  }
                  onClick={() => handleSelectItemEventGa(item)}
                  className={styles['part-number-underline']}
                >
                  {item.partNumber}: {item.name}
                </CatTextLink>
                <div className={styles['truncate-part-desc note-font']}>
                  {item?.longDescription}
                </div>
              </CatTableCell>

              <CatTableCell>
                {item?.price?.enableAddToCart &&
                  (isLoadingInitial || isLoadingRow[index] ? (
                    <SkeletonLoader />
                  ) : (
                    <CommonAvailabilityWrapper
                      availabilityData={
                        dealerPriceData?.[item?.partNumber] ?? item?.price
                      }
                      shouldDisplayLabel={false}
                      isLoading={false}
                      locale={locale}
                      dealerData={userStoreLocationData}
                      dealerInformation={dealerInformation}
                      availRemoveInterimStockFlag={availRemoveInterimStockFlag}
                      isShowSourcingDetailEnabled={isShowSourcingDetailEnabled}
                      isDeliveryEnabled={isDeliveryEnabled}
                    />
                  ))}
              </CatTableCell>
              {isLoadingInitial || isLoadingRow[index] ? (
                <SkeletonLoader className="mt-4" />
              ) : (
                <CatTableCell align="right">
                  <Price
                    price={dealerPriceData?.[item?.partNumber] ?? item?.price}
                    currency={priceAvailabilityDrawerData?.currency}
                    showDiscountSection={false}
                    isRTL={isRTL}
                    currencyLdFeatureFlag={currencyLdFeatureFlag}
                  />
                </CatTableCell>
              )}

              <CatTableCell>
                {item?.price?.enableAddToCart ? (
                  <CatButton
                    disabled={Number(item?.quantity) < 1}
                    variant="primary"
                    onClick={() => handleAddToCart(item)}
                  >
                    {t('ADD_TO_CART')}
                  </CatButton>
                ) : (
                  <>{contactDealerComp}</>
                )}
              </CatTableCell>
              <CatTableCell>
                <span className="m-0">
                  <CatBadge
                    position="top-right"
                    text={getItemCount(item?.partNumber)}
                    variant="brand"
                    data-testid="cat-header-cart-badge"
                    size="md"
                  />
                  <CatIconShopFilled size="lg" />
                </span>
              </CatTableCell>
            </CatTableRow>
          ))}
        </CatTable>
      </CatTableObject>
    </div>
  );
};
export default KitDetailsTableBody;
